import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEye } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { ThreeDots } from "react-loader-spinner";
import SearchFilter from "../../Common/SearchFilter";
import ColumnVisibilityControl from "../../Common/ColumnVisibilityControl";
import ActionButton from "../../Common/ActionButton";
import StatusChip from "../../Common/StatusChip";
import { getallStock } from "../../../Redux/actions/Operationsactions/getstock";
import { getallMaterial } from "../../../Redux/actions/Operationsactions/getmaterial";

/**
 * Stock (at a glance) List Table.
 * Shows current stock levels per material — Stock Adjustment itself (create/edit) isn't
 * built yet, this is the "what's actually in stock" view that has to exist before it.
 */
const StockAdjustmentTable = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const stockData = useSelector((state) => state.getAllStock);
  const materialData = useSelector((state) => state.getAllMaterial?.data || []);
  const rolesData = useSelector((state) => state.GetAllRole);

  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});

  useEffect(() => {
    dispatch(getallStock());
    dispatch(getallMaterial());
  }, [dispatch]);

  /* Merge Stock/GetAll (Quantity/Wac/RestockLevel/Batches) with Material/GetAll
     (CategoryName/UomName) — Stock's own response doesn't carry those. */
  const rows = useMemo(() => {
    const materialById = {};
    materialData.forEach((m) => {
      materialById[m.Id] = m;
    });
    return (stockData?.data || []).map((s) => ({
      ...s,
      CategoryName: materialById[s.MaterialId]?.CategoryName || "",
      UomName: materialById[s.MaterialId]?.UomName || "",
    }));
  }, [stockData?.data, materialData]);

  /* ─── Column Definitions ──────────────────────────────────────────────── */
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Category",
        field: "CategoryName",
        flex: 1,
      },
      {
        headerName: "Item",
        field: "ItemName",
        flex: 1,
      },
      {
        headerName: "UOM",
        field: "UomName",
        flex: 1,
      },
      {
        headerName: "On Hand Qty",
        field: "Quantity",
        flex: 1,
      },
      {
        headerName: "Reserved",
        field: "Reserved",
        flex: 1,
      },
      {
        headerName: "Free",
        field: "Free",
        flex: 1,
      },
      {
        headerName: "WAC",
        field: "Wac",
        flex: 1,
        valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : "N/A"),
      },
      {
        headerName: "Restock Level",
        field: "RestockLevel",
        flex: 1,
      },
      {
        headerName: "Status",
        colId: "belowRestock",
        flex: 1,
        valueGetter: (params) => (params.data?.BelowRestock ? "Below Restock" : "OK"),
        cellRenderer: (params) => {
          if (!params.data) {
            return null;
          }
          const status = params.value;
          const type = status === "Below Restock" ? "warning" : "success";
          return <StatusChip status={status} type={type} />;
        },
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 130,
        filter: false,
        sortable: false,
        suppressMenu: true,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {rolesData?.data?.UserRoles?.includes("Store Stock Adjustments_Create") ? (
              <ActionButton
                text="Adjust Stock"
                newBtn="true"
                onClick={() => history.push("/store/stock/add")}
              />
            ) : (
              <span>Action</span>
            )}
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <FiEye
              title="View"
              size="1.1em"
              style={{ cursor: "pointer", color: "#4ac8d1" }}
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/store/stock/details/${params.data?.MaterialId}`);
              }}
            />
          </div>
        ),
      },
    ],
    [history, rolesData],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "N/A" : p.value,
      minWidth: 100,
    }),
    [],
  );

  /* ─── Column show / hide ──────────────────────────────────────────────── */
  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({
        ...prev,
        [colId]: !isCurrentlyVisible,
      }));
    },
    [columnVisibility],
  );

  /* ─── Render ──────────────────────────────────────────────────────────── */
  return (
    <>
      {/* Toolbar */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Stock"
        >
          Stock
        </div>
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search"
        />

        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
        {stockData?.loading && (
          <div>
            <ThreeDots color="green" height={40} width={40} />
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export { StockAdjustmentTable };
export default StockAdjustmentTable;
